import { ArrowRight } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useIntersectionReveal } from '@/hooks/useIntersectionReveal';
import { serviceImages } from '@/config/serviceImages';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/button';

interface ServiceCardProps {
  image: string;
  srcSet: string;
  title: string;
  description: string;
  route: string;
  index: number;
  imagePosition?: string;
  isVisible: boolean;
  badge?: string;
}

const ServiceCard = ({
  image,
  srcSet,
  title,
  description,
  route,
  index,
  imagePosition = 'center',
  isVisible,
  badge,
}: ServiceCardProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <Link
      to={route}
      className={`reveal-up group relative flex aspect-[4/3] flex-col justify-end overflow-hidden rounded-lg bg-navy p-6 text-white ${
        isVisible ? 'is-visible' : ''
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      {/* Full-bleed image */}
      <img
        src={image}
        srcSet={srcSet}
        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
        alt={`${title} service by Altum Contractors Houston TX`}
        className={`absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-reveal group-hover:scale-[1.04] ${
          imageLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ objectPosition: imagePosition }}
        loading="lazy"
        onLoad={() => setImageLoaded(true)}
      />

      {/* Navy scrim for legibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-navy/95 via-navy/30 to-transparent" />

      {/* Content */}
      <div className="relative">
        {badge && (
          <span className="relative block pl-11 text-[11px] font-semibold uppercase tracking-[0.14em] text-white before:absolute before:-left-6 before:top-1/2 before:h-1 before:w-14 before:-translate-y-1/2 before:bg-primary">
            {badge}
          </span>
        )}
        <h3 className="mt-3 font-heading text-2xl uppercase leading-none">
          {title}
        </h3>
        <p className="mt-2 text-sm text-on-navy line-clamp-2">
          {description}
        </p>
        <span className="mt-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em]">
          Learn More
          <ArrowRight className="w-4 h-4 transition-transform duration-300 ease-reveal group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
};

const ServicesSection = () => {
  const { t } = useTranslation('home');
  const { ref: sectionRef, isVisible } = useIntersectionReveal<HTMLDivElement>();

  const services = [
    {
      ...serviceImages.roofRepairs,
      title: t('services.roofRepairs.title'),
      description: t('services.roofRepairs.description'),
      route: '/services/roof-repairs',
      imagePosition: 'center 35%',
      badge: 'Residential',
    },
    {
      ...serviceImages.roofReplacement,
      title: t('services.roofReplacement.title'),
      description: t('services.roofReplacement.description'),
      route: '/services/roof-replacement',
      imagePosition: 'center',
      badge: 'Installation',
    },
    {
      ...serviceImages.roofMaintenance,
      title: t('services.maintenance.title'),
      description: t('services.maintenance.description'),
      route: '/services/roof-maintenance',
      imagePosition: 'center',
      badge: 'Care',
    },
    {
      ...serviceImages.newConstruction,
      title: t('services.newConstruction.title'),
      description: t('services.newConstruction.description'),
      route: '/services/new-construction',
      imagePosition: 'center',
      badge: 'New Build',
    },
    {
      ...serviceImages.skylightsVentilation,
      title: t('services.enhancements.title'),
      description: t('services.enhancements.description'),
      route: '/services/skylights-ventilation',
      imagePosition: 'center',
      badge: 'Upgrades',
    },
  ];

  return (
    <section id="services" ref={sectionRef} className="py-24 md:py-32 bg-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          eyebrow={t('services.eyebrow')}
          headline={t('services.headline')}
          className="mb-16"
          aside={
            <Button asChild variant="outline" size="lg">
              <Link to="/services">
                View All Services <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          }
        />

        {/* Services Grid - 3 columns on desktop */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {services.slice(0, 3).map((service, index) => (
            <ServiceCard
              key={service.route}
              image={service.src}
              srcSet={service.srcSet}
              title={service.title}
              description={service.description}
              route={service.route}
              index={index}
              imagePosition={service.imagePosition}
              isVisible={isVisible}
              badge={service.badge}
            />
          ))}
        </div>

        {/* Second row - 2 cards centered */}
        <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:mx-auto lg:max-w-[calc(66.666%+0.5rem)]">
          {services.slice(3).map((service, index) => (
            <ServiceCard
              key={service.route}
              image={service.src}
              srcSet={service.srcSet}
              title={service.title}
              description={service.description}
              route={service.route}
              index={index + 3}
              imagePosition={service.imagePosition}
              isVisible={isVisible}
              badge={service.badge}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
